import React, { useState } from 'react';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { autocompletion, completeFromList } from '@codemirror/autocomplete';
import { keymap } from '@codemirror/view';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { linter } from '@codemirror/lint';
import { CodeEditorLanguageType } from '@core/components/form';


export function validateLanguage(type: CodeEditorLanguageType, view: EditorView) {
  switch (type) {
    case CodeEditorLanguageType.PHP:
      return phpValidator(view);
    case CodeEditorLanguageType.EXCEL:
      return excelValidator(view);
    default:
      return null;
  }
}


// List of common Excel functions
const excelFunctions = [
  'SUM',
  'AVERAGE',
  'IF',
  'VLOOKUP',
  'HLOOKUP',
  'INDEX',
  'MATCH',
  'COUNT',
  'COUNTA',
  'MIN',
  'MAX',
  'ROUND',
  'NOW',
  'TODAY',
  'CONCATENATE',
];

// Custom validator function for formulas
export function excelValidator(view) {
  const diagnostics = [];
  const code = view.state.doc.toString();
  const lines = code.split('\n');

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Check if the line is an Excel formula (starts with `=`)
    if (trimmedLine.startsWith('=')) {
      // Extract the formula part after `=`
      const formula = trimmedLine.substring(1).trim();

      // Check for basic formula structure (function names and parentheses)
      const functionName = formula.split('(')[0].toUpperCase();

      if (formula.includes('(') && formula.includes(')')) {
        // Check if the function name is a valid Excel function
        if (!excelFunctions.includes(functionName)) {
          diagnostics.push({
            from: view.state.doc.line(index + 1).from,
            to: view.state.doc.line(index + 1).to,
            severity: 'error',
            message: `Invalid function name: ${functionName}`,
          });
        }
      } else if (formula.includes('(') && !formula.includes(')')) {
        // Missing closing parenthesis
        diagnostics.push({
          from: view.state.doc.line(index + 1).from,
          to: view.state.doc.line(index + 1).to,
          severity: 'error',
          message: 'Missing closing parenthesis in formula.',
        });
      }
    }
  });

  return diagnostics;
}

function phpValidator(view) {
  const diagnostics = [];
  const code = view.state.doc.toString();

  // Simple validation for missing semicolons and invalid assignments
  const lines = code.split('\n');
  lines.forEach((line, i) => {
    const trimmedLine = line.trim();

    // Skip lines that are part of control structures or functions (don't require semicolons)
    const controlStructurePattern = /^(if|else|for|while|function|switch|foreach|do)\b/;
    const blockStartPattern = /\{$/;
    const blockEndPattern = /\}$/;

    if (controlStructurePattern.test(trimmedLine) || blockStartPattern.test(trimmedLine) || blockEndPattern.test(trimmedLine)) {
      // Skip validation for control structures
      return;
    }

    // Check for missing semicolons
    if (trimmedLine && !trimmedLine.endsWith(';') && !trimmedLine.endsWith('}')) {
      diagnostics.push({
        from: view.state.doc.line(i + 1).from,
        to: view.state.doc.line(i + 1).to,
        severity: 'error',
        message: 'Missing semicolon',
      });
    }

    // Check for invalid variable assignments (e.g., $var = d;)
    const variableAssignmentPattern = /^\$[a-zA-Z_]\w*\s*=\s*[^'"\d$]+;$/;
    if (variableAssignmentPattern.test(trimmedLine)) {
      diagnostics.push({
        from: view.state.doc.line(i + 1).from,
        to: view.state.doc.line(i + 1).to,
        severity: 'error',
        message: 'Invalid assignment, expected a string, number, or variable.',
      });
    }
  });

  return diagnostics;
}
